/**
 * @typedef {Object} MonthVolumeQuantity
 * @property {number} month Month index starting from 1 as January
 * @property {number} quantity Quantity of volume in MW for that month
 */

/**
 * @typedef {CONTRACTED_VOLUME_PRODUCT_TYPE|BUYING_VOLUME_PRODUCT_TYPE} ProductTypeDef
 */

/**
 * @typedef {Object} ProductVolumeDef
 * @property {ProductTypeDef} productType
 * @property {MonthVolumeQuantity[]} quantities quantities
 */

import { getQuarterMonthIndexes } from './date';

/**
 *
 * @typedef {ProductVolumeDef[]} Volumes
 */

const PRODUCT_ID_MONTH_PREFIX = 'M';
const PRODUCT_ID_QUARTER_PREFIX = 'Q';

export const CONTRACTED_VOLUME_PRODUCT_TYPE = 'CONTRACT';
export const BUYING_VOLUME_PRODUCT_TYPE = 'BUYING';
export const TRADING_VOLUME_PRODUCT_TYPE = 'TRADING';
export const RELEASING_VOLUME_PRODUCT_TYPE = 'RELEASING';
export const REMAINING_VOLUME_PRODUCT_TYPE = 'REMAINING';

const VOLUME_PRECISION = 3;
const PERCENTAGE_PRECISION = 3;

/**
 * @param {ProductVolumeDef[]} products Products
 * @param {string} productType ProductType
 * @returns {boolean} If it contains product type
 */
export const containsProductType = (products, productType) => {
  const index = products.findIndex((product) => product.productType === productType);
  return index !== -1;
};

/**
 * @param {Volumes} volumes Volumes
 * @returns {ProductVolumeDef} Contracted volume
 */
export const getContractedVolume = (volumes) => {
  return getProductVolume(volumes, CONTRACTED_VOLUME_PRODUCT_TYPE);
};

export const getProductVolume = (volumes, productType) => {
  return volumes.find((volume) =>
    volume.productType === productType);
};

/**
 * @param {ProductVolumeDef[]} products Products to change
 * @param {ProductVolumeDef} productToAdd Buying product to replace
 * @returns {ProductVolumeDef[]} Changed product
 */
export const addOrReplaceProduct = (products, productToAdd) => {
  const index = products.findIndex((product) => {
    return product.productType === productToAdd.productType;
  });
  if (index === -1) {
    return [...products, productToAdd];
  } else {
    const copy = [...products];
    copy[index] = productToAdd;
    return copy;
  }
};

export const getFilteredProductQuantity = (contractedProduct, product) => {
  const productTypeId = product.productType.id;
  if (productTypeId.startsWith(PRODUCT_ID_MONTH_PREFIX)) {
    const monthIndex = Number(productTypeId.substring(PRODUCT_ID_MONTH_PREFIX.length));
    contractedProduct = {
      productType: contractedProduct.productType,
      quantities: filterQuantitiesByMonthIndexes(contractedProduct.quantities, [monthIndex - 1]),
    };
  } else if (productTypeId.startsWith(PRODUCT_ID_QUARTER_PREFIX)) {
    const quarterIndex = Number(productTypeId.substring(PRODUCT_ID_MONTH_PREFIX.length));
    const monthIndexes = getQuarterMonthIndexes(quarterIndex - 1);
    contractedProduct = {
      productType: contractedProduct.productType,
      quantities: filterQuantitiesByMonthIndexes(contractedProduct.quantities, monthIndexes),
    };
  }
  return contractedProduct;
};

/**
 * @param {MonthVolumeQuantity[]} quantities MonthVolumeQuantities
 * @param {number[]} monthIndexes Month index, Jan=0
 * @returns {MonthVolumeQuantity[]} MonthVolumeQuantity
 */
export const filterQuantitiesByMonthIndexes = (quantities, monthIndexes) => {
  return quantities.filter((monthQuantity) => {
    return monthIndexes.includes(monthQuantity.month - 1);
  });
};

/**
 * @param {Big} volume Volume Big
 * @returns {number} Converted to number with fixed dp
 */
export const volumeBigToNumber = (volume) => {
  return Number(volume.toFixed(VOLUME_PRECISION));
};

export const percentageBigToNumber = (percentage) => {
  return Number(percentage.toFixed(PERCENTAGE_PRECISION));
};
